<template>
  <div class="registration-map-dashboard" v-loading="loading">
    <div class="dashboard-header">
      <div>
        <p>{{ currentScopeText }} · 报名订单省份分布</p>
      </div>
      <div class="header-tools">
        <div class="scope-switch">
          <button
            type="button"
            class="scope-button"
            :class="{ active: scopeMode === 'unfinished' }"
            @click="selectScopeMode('unfinished')"
          >未完赛赛事</button>
          <button
            type="button"
            class="scope-button"
            :class="{ active: scopeMode === 'year' }"
            @click="selectScopeMode('year')"
          >今年赛事</button>
        </div>
        <el-date-picker
          v-if="scopeMode === 'year'"
          v-model="selectedYear"
          type="year"
          size="small"
          value-format="yyyy"
          :clearable="false"
          class="year-picker"
          placeholder="选择年份"
          @change="fetchDashboard"
        ></el-date-picker>
        <div class="data-type-switch">
          <button
            type="button"
            class="data-type-button"
            :class="{ active: dataType === 'orders' }"
            @click="selectDataType('orders')"
          >报名订单数</button>
          <button
            type="button"
            class="data-type-button"
            :class="{ active: dataType === 'events' }"
            @click="selectDataType('events')"
          >赛事数量</button>
        </div>
        <el-button
          type="primary"
          icon="el-icon-refresh"
          size="small"
          :loading="loading"
          @click="fetchDashboard"
        >刷新</el-button>
      </div>
    </div>

    <div class="metric-grid">
      <div class="metric-card">
        <el-tooltip :content="`${currentScopeText} · 已支付 + 免费报名`" placement="top">
          <div class="metric-icon">
            <i class="el-icon-location-outline"></i>
          </div>
        </el-tooltip>
        <div>
          <div class="metric-label">当前报名订单数</div>
          <div class="metric-value">{{ dashboard.totalRegistrationCount }}</div>
        </div>
      </div>
      <div class="metric-card">
        <el-tooltip :content="currentEventCountTip" placement="top">
          <div class="metric-icon metric-icon-secondary">
            <i class="el-icon-trophy"></i>
          </div>
        </el-tooltip>
        <div>
          <div class="metric-label">{{ currentEventCountLabel }}</div>
          <div class="metric-value">{{ currentEventCount }}</div>
        </div>
      </div>
      <div class="metric-card">
        <el-tooltip content="enableRegister 为 true 或 configId 非空" placement="top">
          <div class="metric-icon metric-icon-service">
            <i class="el-icon-s-check"></i>
          </div>
        </el-tooltip>
        <div>
          <div class="metric-label">开通报名服务赛事数量</div>
          <div class="metric-value">{{ registrationServiceEventCount }}</div>
        </div>
      </div>
      <div class="metric-card">
        <el-tooltip :content="`开通报名服务赛事数量 / ${currentEventCountLabel}`" placement="top">
          <div class="metric-icon metric-icon-ratio">
            <i class="el-icon-pie-chart"></i>
          </div>
        </el-tooltip>
        <div>
          <div class="metric-label">合作赛事占比</div>
          <div class="metric-value">{{ cooperationEventRatio }}</div>
        </div>
      </div>
      <div class="metric-card">
        <el-tooltip :content="`存在${currentScopeText}的省份数量`" placement="top">
          <div class="metric-icon metric-icon-region">
            <i class="el-icon-map-location"></i>
          </div>
        </el-tooltip>
        <div>
          <div class="metric-label">覆盖省份</div>
          <div class="metric-value">{{ activeProvinceCount }}</div>
        </div>
      </div>
      <div class="metric-card">
        <el-tooltip :content="`地址暂未识别出省份的${currentScopeText}数量`" placement="top">
          <div class="metric-icon metric-icon-tertiary">
            <i class="el-icon-warning-outline"></i>
          </div>
        </el-tooltip>
        <div>
          <div class="metric-label">待识别赛事</div>
          <div class="metric-value">{{ dashboard.unknownEvents.length }}</div>
        </div>
      </div>
    </div>

    <div class="map-layout">
      <section class="map-panel">
        <div class="panel-title">
          <div>
            <h3>中国赛事地图</h3>
            <span>{{ currentDataTypeText }}省份分布，点击省份查看具体赛事</span>
          </div>
          <el-tag size="small" effect="plain">单位：{{ currentDataUnit }}</el-tag>
        </div>
        <div ref="chinaMap" class="china-map"></div>
      </section>

      <section class="rank-panel">
        <div class="panel-title compact">
          <div>
            <h3>省份排行</h3>
            <span>按{{ currentDataTypeText }}排序</span>
          </div>
        </div>
        <el-empty
          v-if="!sortedProvinceStats.length"
          :description="`暂无${currentScopeText}报名数据`"
          :image-size="80"
        ></el-empty>
        <div v-else class="rank-list">
          <button
            v-for="(item, index) in sortedProvinceStats"
            :key="item.province"
            class="rank-item"
            type="button"
            @click="openProvince(item.province)"
          >
            <span class="rank-index">{{ index + 1 }}</span>
            <span class="rank-name">{{ item.province }}</span>
            <span class="rank-count">{{ getProvinceValue(item) }}{{ currentDataUnit }}</span>
          </button>
        </div>
      </section>
    </div>

    <el-dialog
      :title="selectedProvince ? `${selectedProvince}赛事报名人数` : '赛事报名人数'"
      :visible.sync="dialogVisible"
      :fullscreen="isMobile"
      width="760px"
      custom-class="province-dialog"
    >
      <el-empty
        v-if="!selectedProvinceEvents.length"
        :description="`该省份暂无${currentScopeText}报名数据`"
        :image-size="90"
      ></el-empty>
      <template v-else>
        <el-table
          v-if="!isMobile"
          :data="selectedProvinceEvents"
          border
          size="small"
        >
          <el-table-column prop="eventName" label="赛事名称" min-width="220">
            <template slot-scope="scope">
              <copy-text :value="scope.row.eventName"></copy-text>
            </template>
          </el-table-column>
          <el-table-column label="比赛时间" width="150">
            <template slot-scope="scope">{{ formatEventDate(scope.row) }}</template>
          </el-table-column>
          <el-table-column prop="address" label="地址" min-width="180"></el-table-column>
          <el-table-column prop="registrationCount" label="报名人数" width="100" align="right"></el-table-column>
        </el-table>
        <div v-else class="mobile-event-list">
          <div
            v-for="event in selectedProvinceEvents"
            :key="event.eventId"
            class="mobile-event-item"
          >
            <div class="mobile-event-title">
              <copy-text :value="event.eventName"></copy-text>
            </div>
            <div class="mobile-event-meta">{{ formatEventDate(event) }}</div>
            <div class="mobile-event-meta">{{ event.address || '暂无地址' }}</div>
            <div class="mobile-event-count">{{ event.registrationCount }} 人</div>
          </div>
        </div>
      </template>
      <span slot="footer" class="dialog-footer">
        <el-button type="primary" @click="dialogVisible = false">关闭</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import * as echarts from 'echarts';
import ThemeManager from '@/utils/theme.js';
import chinaGeoJson from '@/assets/map/china.json';

const provinceNameMap = {
  北京: '北京市',
  天津: '天津市',
  上海: '上海市',
  重庆: '重庆市',
  河北: '河北省',
  山西: '山西省',
  辽宁: '辽宁省',
  吉林: '吉林省',
  黑龙江: '黑龙江省',
  江苏: '江苏省',
  浙江: '浙江省',
  安徽: '安徽省',
  福建: '福建省',
  江西: '江西省',
  山东: '山东省',
  河南: '河南省',
  湖北: '湖北省',
  湖南: '湖南省',
  广东: '广东省',
  海南: '海南省',
  四川: '四川省',
  贵州: '贵州省',
  云南: '云南省',
  陕西: '陕西省',
  甘肃: '甘肃省',
  青海: '青海省',
  台湾: '台湾省',
  内蒙古: '内蒙古自治区',
  广西: '广西壮族自治区',
  西藏: '西藏自治区',
  宁夏: '宁夏回族自治区',
  新疆: '新疆维吾尔自治区',
  香港: '香港特别行政区',
  澳门: '澳门特别行政区'
};
const fullNameToShort = Object.keys(provinceNameMap).reduce((map, name) => {
  map[provinceNameMap[name]] = name;
  return map;
}, {});
const emptyDashboard = () => ({
  timezone: 'Asia/Shanghai',
  updatedAt: '',
  scopeMode: 'unfinished',
  year: null,
  scopeLabel: '未完赛赛事',
  totalEventCount: null,
  totalRegistrationCount: 0,
  totalUnfinishedEventCount: 0,
  registrationServiceEventCount: 0,
  provinceStats: [],
  eventsByProvince: {},
  unknownEvents: []
});

echarts.registerMap('china', chinaGeoJson);

export default {
  name: 'RegistrationMapDashboard',
  data() {
    return {
      loading: false,
      dashboard: emptyDashboard(),
      scopeMode: 'unfinished',
      selectedYear: new Date().getFullYear().toString(),
      dataType: 'orders',
      chart: null,
      dialogVisible: false,
      selectedProvince: '',
      unsubscribeTheme: null,
      resizeTimer: null,
      isMobile: false
    };
  },
  computed: {
    sortedProvinceStats() {
      return [...(this.dashboard.provinceStats || [])].sort((a, b) => {
        return this.getProvinceValue(b) - this.getProvinceValue(a) || b.eventCount - a.eventCount || b.count - a.count;
      });
    },
    activeProvinceCount() {
      return this.sortedProvinceStats.filter(item => Number(item.eventCount || 0) > 0).length;
    },
    currentScopeText() {
      if (this.scopeMode === 'year') {
        return `${this.selectedYear}年赛事`;
      }
      return '未完赛赛事';
    },
    currentEventCountLabel() {
      return this.scopeMode === 'year' ? '当前年份赛事数量' : '当前待举办赛事数量';
    },
    currentEventCountTip() {
      return this.scopeMode === 'year' ? '比赛开始时间属于所选年份' : '比赛结束时间未过';
    },
    currentEventCount() {
      if (Number.isFinite(Number(this.dashboard.totalEventCount))) {
        return Number(this.dashboard.totalEventCount || 0);
      }
      if (Number.isFinite(Number(this.dashboard.totalUnfinishedEventCount))) {
        return Number(this.dashboard.totalUnfinishedEventCount || 0);
      }
      const recognizedEventCount = (this.dashboard.provinceStats || []).reduce((sum, item) => {
        return sum + Number(item.eventCount || 0);
      }, 0);
      return recognizedEventCount + (this.dashboard.unknownEvents || []).length;
    },
    registrationServiceEventCount() {
      return Number(this.dashboard.registrationServiceEventCount || 0);
    },
    cooperationEventRatio() {
      if (!this.currentEventCount) return '0%';
      return `${((this.registrationServiceEventCount / this.currentEventCount) * 100).toFixed(1)}%`;
    },
    selectedProvinceEvents() {
      if (!this.selectedProvince) return [];
      return this.dashboard.eventsByProvince[this.selectedProvince] || [];
    },
    maxProvinceCount() {
      return Math.max(...this.sortedProvinceStats.map(item => this.getProvinceValue(item)), 0);
    },
    currentDataTypeText() {
      return this.dataType === 'events' ? '赛事数量' : '报名订单数';
    },
    currentDataUnit() {
      return this.dataType === 'events' ? '场' : '单';
    }
  },
  mounted() {
    this.updateViewport();
    this.fetchDashboard();
    window.addEventListener('resize', this.handleResize);
    this.unsubscribeTheme = ThemeManager.subscribe(() => {
      this.renderMap();
    });
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    if (this.unsubscribeTheme) {
      this.unsubscribeTheme();
    }
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
    }
    if (this.chart) {
      this.chart.off('click');
      this.chart.dispose();
      this.chart = null;
    }
  },
  methods: {
    fetchDashboard() {
      this.loading = true;
      this.$axios
        .get('/form-result/registration_map_dashboard', {
          params: {
            mode: this.scopeMode,
            year: this.selectedYear
          },
          headers: {
            'x-auth-token': localStorage.getItem('bde_token')
          }
        })
        .then((res) => {
          this.dashboard = {
            ...emptyDashboard(),
            ...(res.data.res || {})
          };
          this.$nextTick(() => {
            this.renderMap();
          });
        })
        .catch((err) => {
          this.$message.error(err.response?.data?.message || '地图大盘加载失败');
          if (err.response?.data?.code === 'token_timeout') {
            this.$router.push('/login');
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    updateViewport() {
      this.isMobile = window.innerWidth <= 768;
    },
    handleResize() {
      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer);
      }
      this.resizeTimer = setTimeout(() => {
        this.updateViewport();
        if (this.chart) {
          this.chart.resize();
          this.renderMap();
        }
      }, 120);
    },
    openProvince(province) {
      this.selectedProvince = province;
      this.dialogVisible = true;
    },
    formatEventDate(event) {
      if (event.fuzzyTime) return event.fuzzyTime;
      const start = event.startTime ? this.$dayjs(event.startTime).format('YYYY-MM-DD') : '';
      const end = event.endTime ? this.$dayjs(event.endTime).format('YYYY-MM-DD') : '';
      if (start && end && start !== end) return `${start} 至 ${end}`;
      return start || end || '--';
    },
    getProvinceValue(item) {
      if (this.dataType === 'events') {
        return Number(item.eventCount || 0);
      }
      return Number(item.count || 0);
    },
    selectDataType(type) {
      if (this.dataType === type) return;
      this.dataType = type;
      this.renderMap();
    },
    selectScopeMode(mode) {
      if (this.scopeMode === mode) return;
      this.scopeMode = mode;
      this.fetchDashboard();
    },
    renderMap() {
      if (!this.$refs.chinaMap) return;
      if (!this.chart) {
        this.chart = echarts.init(this.$refs.chinaMap);
        this.chart.on('click', (params) => {
          const province = fullNameToShort[params.name] || params.name;
          if (province) {
            this.openProvince(province);
          }
        });
      }

      const isDark = ThemeManager.isDark();
      const textColor = isDark ? '#e6edf3' : '#303133';
      const borderColor = isDark ? '#30363d' : '#ffffff';
      const areaColor = isDark ? '#161b22' : '#f2f6fc';
      const emphasisColor = isDark ? '#30363d' : '#fdecee';
      const data = (this.dashboard.provinceStats || []).map(item => ({
        name: provinceNameMap[item.province] || item.province,
        province: item.province,
        value: this.getProvinceValue(item),
        count: Number(item.count || 0),
        eventCount: Number(item.eventCount || 0)
      }));
      const dataMap = data.reduce((map, item) => {
        map[item.name] = item;
        return map;
      }, {});

      this.chart.setOption({
        tooltip: {
          trigger: 'item',
          formatter: (params) => {
            const item = dataMap[params.name];
            const province = fullNameToShort[params.name] || params.name;
            if (!item) {
              return `${province}<br/>报名订单数：0<br/>${this.currentScopeText}：0 场`;
            }
            return `${province}<br/>报名订单数：${item.count}<br/>${this.currentScopeText}：${item.eventCount} 场`;
          }
        },
        visualMap: {
          min: 0,
          max: Math.max(this.maxProvinceCount, 1),
          left: this.isMobile ? 8 : 18,
          bottom: this.isMobile ? 8 : 18,
          itemWidth: this.isMobile ? 10 : 14,
          itemHeight: this.isMobile ? 80 : 110,
          text: ['高', '低'],
          textStyle: {
            color: textColor
          },
          inRange: {
            color: ['#f8d7da', '#e58b93', '#c04851']
          }
        },
        series: [
          {
            type: 'map',
            map: 'china',
            roam: !this.isMobile,
            zoom: this.isMobile ? 1.12 : 1.08,
            top: this.isMobile ? 24 : 18,
            bottom: this.isMobile ? 28 : 18,
            data,
            itemStyle: {
              areaColor,
              borderColor,
              borderWidth: 1
            },
            emphasis: {
              label: {
                color: textColor
              },
              itemStyle: {
                areaColor: emphasisColor
              }
            },
            label: {
              show: true,
              color: textColor,
              fontSize: this.isMobile ? 9 : 11,
              formatter: (params) => {
                const item = dataMap[params.name];
                if (!item || !item.value) return '';
                return `${fullNameToShort[params.name] || params.name}\n${item.value}`;
              }
            }
          }
        ]
      }, true);

      this.chart.resize();
    }
  }
};
</script>

<style scoped>
.registration-map-dashboard {
  color: var(--text-primary);
}

.dashboard-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}

.panel-title h3 {
  margin: 0;
  color: var(--text-primary);
  font-weight: 700;
}

.dashboard-header p,
.panel-title span {
  margin: 6px 0 0;
  color: var(--text-secondary);
  font-size: 13px;
}

.dashboard-header p {
  margin-top: 0;
}

.header-tools {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 10px;
}

.scope-switch,
.data-type-switch {
  display: inline-grid;
  overflow: hidden;
  border: 1px solid var(--border-lighter);
  border-radius: 8px;
  background: var(--bg-card);
}

.scope-switch {
  grid-template-columns: repeat(2, minmax(104px, 1fr));
}

.data-type-switch {
  grid-template-columns: repeat(2, minmax(112px, 1fr));
}

.year-picker {
  width: 118px;
}

.scope-button,
.data-type-button {
  min-height: 32px;
  border: 0;
  border-right: 1px solid var(--border-lighter);
  border-radius: 0;
  background: transparent;
  color: var(--text-regular);
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;
  padding: 0 14px;
}

.scope-button:hover,
.data-type-button:hover {
  color: var(--brand-primary);
}

.scope-button:last-child,
.data-type-button:last-child {
  border-right: 0;
}

.scope-button:first-child,
.data-type-button:first-child {
  border-radius: 7px 0 0 7px;
}

.scope-button:last-child,
.data-type-button:last-child {
  border-radius: 0 7px 7px 0;
}

.scope-button.active,
.data-type-button.active {
  background: var(--brand-primary);
  color: #fff;
  box-shadow: inset 0 0 0 1px var(--brand-primary);
}

.dark-mode .scope-switch,
.dark-mode .data-type-switch {
  border-color: var(--border-lighter);
  background: var(--bg-page);
}

.dark-mode .scope-button,
.dark-mode .data-type-button {
  border-color: var(--border-lighter);
  color: var(--text-regular);
}

.dark-mode .scope-button.active,
.dark-mode .data-type-button.active {
  background: rgba(var(--brand-primary-rgb), 0.24);
  color: var(--brand-primary-light);
  box-shadow: inset 0 0 0 1px rgba(var(--brand-primary-rgb), 0.62);
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 14px;
  margin-bottom: 16px;
}

.metric-card,
.map-panel,
.rank-panel {
  border: 1px solid var(--border-lighter);
  border-radius: 8px;
  background: var(--bg-card);
  box-shadow: var(--shadow-light);
}

.metric-card {
  display: flex;
  align-items: center;
  gap: 14px;
  box-sizing: border-box;
  min-height: 118px;
  padding: 20px;
}

.metric-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 48px;
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background: #c04851;
  color: #fff;
  font-size: 24px;
}

.metric-icon-secondary {
  background: #2f80ed;
}

.metric-icon-service {
  background: #6f42c1;
}

.metric-icon-ratio {
  background: #d67b00;
}

.metric-icon-region {
  background: #16a085;
}

.metric-icon-tertiary {
  background: #e6a23c;
}

.metric-label {
  color: var(--text-regular);
  font-size: 14px;
}

.metric-value {
  margin-top: 6px;
  color: var(--text-primary);
  font-size: 28px;
  font-weight: 700;
  line-height: 1.15;
}

.map-layout {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(280px, 0.8fr);
  align-items: start;
  gap: 16px;
}

.map-panel,
.rank-panel {
  box-sizing: border-box;
  min-width: 0;
  padding: 18px;
}

.rank-panel {
  display: flex;
  flex-direction: column;
  max-height: 634px;
}

.panel-title {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.panel-title.compact {
  margin-bottom: 12px;
}

.panel-title h3 {
  font-size: 17px;
}

.china-map {
  width: 100%;
  height: 560px;
}

.rank-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow-y: auto;
  padding-right: 4px;
}

.rank-list::-webkit-scrollbar {
  width: 6px;
}

.rank-list::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: var(--border-base);
}

.rank-list::-webkit-scrollbar-track {
  background: transparent;
}

.rank-item {
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr) 52px 42px;
  align-items: center;
  gap: 8px;
  width: 100%;
  border: 0;
  border-radius: 8px;
  background: var(--bg-page);
  color: var(--text-regular);
  cursor: pointer;
  padding: 10px;
  text-align: left;
}

.rank-item:hover {
  color: var(--brand-primary);
}

.rank-index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: var(--brand-primary-lighter);
  color: var(--brand-primary);
  font-size: 12px;
  font-weight: 700;
}

.rank-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rank-count {
  color: var(--text-primary);
  font-weight: 700;
  text-align: right;
}

.mobile-event-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.mobile-event-item {
  position: relative;
  border: 1px solid var(--border-lighter);
  border-radius: 8px;
  padding: 12px 76px 12px 12px;
  background: var(--bg-page);
}

.mobile-event-title {
  color: var(--text-primary);
  font-weight: 700;
  line-height: 1.4;
}

.mobile-event-meta {
  margin-top: 6px;
  color: var(--text-secondary);
  font-size: 13px;
  line-height: 1.4;
}

.mobile-event-count {
  position: absolute;
  top: 12px;
  right: 12px;
  color: var(--brand-primary);
  font-size: 18px;
  font-weight: 700;
}

@media (max-width: 1024px) {
  .metric-grid,
  .map-layout {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .dashboard-header {
    flex-direction: column;
  }

  .header-tools {
    width: 100%;
    justify-content: stretch;
  }

  .scope-switch,
  .data-type-switch {
    flex: 1 1 auto;
    width: 100%;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .year-picker {
    flex: 1 1 120px;
    width: auto;
  }

  .header-tools > .el-button {
    flex: 0 0 76px;
  }

  .metric-card,
  .map-panel,
  .rank-panel {
    padding: 14px;
  }

  .rank-panel {
    max-height: none;
  }

  .rank-list {
    overflow-y: visible;
    padding-right: 0;
  }

  .metric-card {
    min-height: 104px;
  }

  .metric-value {
    font-size: 24px;
  }

  .china-map {
    height: 420px;
  }
}
</style>
